import { AnimatePresence, motion } from "motion/react";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import HomeFilters from "./home-filters/HomeFilters";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { VideoModal } from "components/modal/video-modal/VideoModal";
import CurrentPlan from "components/products/current-plan/CurrentPlan";
import ProductCard from "components/products/product-card/ProductCard";
import ProductGuideHelp from "components/products/product-guide-help/ProductGuideHelp";
import style from "components/products/products.scss";
import Support from "components/products/support/Support";
import { filterProductManuals } from "components/support/support-and-help/ProductManuals";
import { filterSoftwarePackages } from "components/support/support-and-help/SoftwarePackages";
import Heading from "components/typography/heading/Heading";
import { AUTH_LICENSE_VIEW_OWN } from "domain/authority";
import { LicenseData } from "domain/licenses";
import { buildOnboardingState } from "domain/onboardings";
import { ProductManual } from "domain/productManuals";
import { OnboardingImage, SoftwarePackage, SoftwarePackageHomePage } from "domain/softwarePackages";
import { licenseService } from "services/licenses/LicenseService";
import { onboardingsService } from "services/onboardings/OnboardingsService";
import { productManualsService } from "services/product-manuals/ProductManualsService";
import { softwarePackagesService } from "services/software-packages/SoftwarePackagesService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import {
    getCurrentTenantDetails,
    hasTenantCookie,
    isInTenantAccess,
    isLicensingModelBundleWithToken,
} from "services/tenants/tenantCookieService";
import { userSessionService } from "services/user/UserSessionService";
import { StoreState } from "store";
import { isStringNotBlank } from "utils/commonFunctions";
import { logger } from "utils/logging";
import { getObject, RepositoryKey } from "utils/repository";

const mapState = (state: StoreState) => ({
    userDetail: state.userReducer.user,
    tenantDetails: state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1],
});

const connector = connect(mapState);
type Props = ConnectedProps<typeof connector>;

export function splitByVersion(groupedProducts: Record<string, SoftwarePackage[]>) {
    const result: Record<string, SoftwarePackage[]> = {};
    Object.entries(groupedProducts).forEach(([groupName, softwarePackages]) => {
        const allVersions = softwarePackages.flatMap((softwarePackage) =>
            softwarePackage.versions.map((v) => v.version)
        );
        const uniqueVersions = new Set(allVersions);
        if (uniqueVersions.size > 1) {
            softwarePackages.forEach((softwarePackage) => {
                softwarePackage.versions.forEach((version) => {
                    const versionMatch = version.version.match(/^([\d.]+)(.*)$/);
                    const versionNumber = versionMatch ? versionMatch[1] : version.version;
                    const versionSuffix = versionMatch ? versionMatch[2].trim() : "";
                    const versionKey = versionSuffix ? `${groupName} ${versionSuffix}` : groupName;
                    if (!result[versionKey]) {
                        result[versionKey] = [];
                    }
                    const modifiedVersion = { ...version, version: versionNumber };
                    result[versionKey].push({ ...softwarePackage, versions: [modifiedVersion] });
                });
            });
        } else {
            result[groupName] = softwarePackages;
        }
    });
    return result;
}

export function appendManuals(products: Set<SoftwarePackageHomePage>, manuals: ProductManual[]) {
    products.forEach((product) => {
        manuals.forEach((manual) => {
            if (typeof product.bmpHomeGroup !== "undefined" && isStringNotBlank(product.bmpHomeGroup)) {
                if (
                    product.productName === "Blancco WinEraser Volume Edition for ARM" &&
                    manual.productName === "Blancco WinEraser"
                ) {
                    product.manualsDownloads = manual;
                }
                if (product.bmpHomeGroup === manual.productName) {
                    product.manualsDownloads = manual;
                }
            } else {
                if (product.productName.includes(manual.productName)) {
                    product.manualsDownloads = manual;
                }
            }
        });
    });
    return products;
}

const Products = ({ userDetail, tenantDetails }: Props): JSX.Element => {
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const initialLoadComplete = React.useRef(false);
    const [mainProductsState, setMainProductsState] = useState<Record<string, SoftwarePackage[]>>({});
    const [additionalProductsState, setAdditionalProductsState] = useState<Record<string, SoftwarePackage[]>>();
    const [displayedProducts, setDisplayedProducts] = useState<Record<string, SoftwarePackage[]>>();
    const [loading, setLoading] = React.useState(true);
    const currentTenant = getCurrentTenantDetails();
    const [fetchedSoftwarePackages, setFetchedSoftwarePackages] = React.useState<SoftwarePackage[]>([]);
    const [fetchedOnboardingImages, setFetchedOnboardingImages] = React.useState<OnboardingImage[]>([]);
    const [licenseList, setLicenseList] = React.useState<LicenseData[]>([]);
    const { t } = useTranslation();
    const [bdeOnboarded, setBdeOnboarded] = React.useState(false);
    const [lastCompletedBdeStep, setLastCompletedBdeStep] = React.useState("");
    const [lastCompletedBmdeStep, setLastCompletedBmdeStep] = React.useState("");
    const [lastCompletedBmpopStep, setLastCompletedBmpopStep] = React.useState("");
    const [lastCompletedBarStep, setLastCompletedBarStep] = React.useState("");
    const [lastCompletedBmweStep, setLastCompletedBmweStep] = React.useState("");
    const [bmdeOnboarded, setBmdeOnboarded] = React.useState(false);
    const [bmpOpOnboarded, setBmpOpOnboarded] = React.useState(false);
    const [barOnboarded, setBarOnboarded] = React.useState(false);
    const [bmweOnboarded, setBmweOnboarded] = React.useState(false);
    const [showTrialVideo, setShowTrialVideo] = React.useState(false);

    function groupProducts(products: Set<SoftwarePackageHomePage>) {
        return [...products].reduce<Record<string, SoftwarePackage[]>>((result, product) => {
            const groupName = product.bmpHomeGroup || product.productName;
            if (!result[groupName]) {
                result[groupName] = [];
            }
            result[groupName].push(product);
            return result;
        }, {});
    }

    const fetchProductsData = async () => {
        const licensesAbortController = new AbortController();
        abortControllers.push(licensesAbortController);
        const licensePromise = licenseService.fetchAllLicenses({
            abortController: licensesAbortController,
            tenantIdentifier: currentTenant.uuid,
            own: !hasTenantCookie(),
        });
        const manualsPromise = productManualsService.fetchProductManuals();

        if (userSessionService.userHasAllAuthorities([AUTH_LICENSE_VIEW_OWN])) {
            const softwarePackagesPromise = softwarePackagesService.fetchSoftwarePackages();
            const [softwarePackagesData, licenseData, manualsData] = await Promise.all([
                softwarePackagesPromise,
                licensePromise,
                manualsPromise,
            ]);

            const filteredProducts = filterSoftwarePackages(
                softwarePackagesData.productDownloads,
                licenseData.licenses.licenseData
            );

            setFetchedSoftwarePackages(filteredProducts);
            setLicenseList(licenseData.licenses.licenseData);
            setFetchedOnboardingImages(softwarePackagesData.onboardingImages);

            let mainProducts: Set<SoftwarePackageHomePage> = new Set();
            let additionalProducts: Set<SoftwarePackageHomePage> = new Set();

            filteredProducts.map((product: SoftwarePackageHomePage) => {
                if (product.bmpHomeCategory === "additional") {
                    if (product.productName != "Blancco WinBuilder") {
                        additionalProducts.add(product);
                    }
                } else if (product.bmpHomeCategory) {
                    mainProducts.add(product);
                }
            });

            const filteredManuals = filterProductManuals(manualsData.productManuals, licenseData.licenses.licenseData);
            mainProducts = appendManuals(mainProducts, filteredManuals);
            additionalProducts = appendManuals(additionalProducts, filteredManuals);

            const groupedMainProducts = splitByVersion(groupProducts(mainProducts));

            setMainProductsState(groupedMainProducts);
            setAdditionalProductsState(splitByVersion(groupProducts(additionalProducts)));
        }
    };

    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            try {
                const promises = [fetchProductsData()];

                if (!isInTenantAccess()) {
                    promises.push(
                        onboardingsService
                            .fetchOnboardedProducts()
                            .then((onboarding) => {
                                const state = buildOnboardingState(onboarding);
                                setLastCompletedBdeStep(state.lastCompletedBdeStep);
                                setBdeOnboarded(state.bdeOnboarded);
                                setLastCompletedBmdeStep(state.lastCompletedBmdeStep);
                                setBmdeOnboarded(state.bmdeOnboarded);
                                setLastCompletedBmpopStep(state.lastCompletedBmpOpStep);
                                setBmpOpOnboarded(state.bmpOpOnboarded);
                                setLastCompletedBarStep(state.lastCompletedBarStep);
                                setBarOnboarded(state.barOnboarded);
                                setLastCompletedBmweStep(state.lastCompletedBmweStep);
                                setBmweOnboarded(state.bmweOnboarded);
                            })
                            .catch(() => {
                                logger.error("Error when fetching the onboarded products.");
                                setLoading(false);
                            })
                    );
                }

                await Promise.all(promises);
            } catch {
                logger.error("Error when fetching the onboarded products.");
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
        const userTotalLoginCount = userDetail?.totalLogins;
        const tenantContractType = tenantDetails?.contractType;
        const viewTrialVideoPopup = getObject(RepositoryKey.TRIAL_ONBOARDING_POPUP) ?? false;
        if (tenantContractType === "TRIAL") {
            if (userTotalLoginCount === 1 && viewTrialVideoPopup == true) {
                setShowTrialVideo(true);
            } else {
                setShowTrialVideo(false);
            }
        }
    }, []);

    const modal = showTrialVideo && (
        <VideoModal
            isOpen={showTrialVideo}
            hideModal={() => {
                setShowTrialVideo(false);
                usageStatisticsService.sendEvent({
                    category: Category.TRIAL_ONBOARDING_VIDEO,
                    action: Action.CLOSE_TRIAL_ONBOARDING_VIDEO,
                });
            }}
            className={style.trialVideoBase}
            linkUrl={"https://blancco.storylane.io/share/bmp-gettingstarted"}
        />
    );
    const additionsProductCards = additionalProductsState && Object.keys(additionalProductsState).length > 0 && (
        <>
            <div className={style.headerWrapper}>
                <Heading tag={"div"} className={style.h3HeaderAdditionalProducts}>
                    {t("Onboarding.home.additionalToolsHeader")}
                </Heading>
            </div>
            {Object.entries(additionalProductsState).map(([key, value]) => (
                <div key={key} className={style.tableCell}>
                    <ProductCard
                        additionalProduct
                        name={key}
                        softwarePackages={value}
                        fetchedSoftwarePackages={fetchedSoftwarePackages}
                        fetchedLicenses={licenseList}
                        bdeOnboarded={bdeOnboarded}
                        bmdeOnboarded={bmdeOnboarded}
                        saveBdeOnboarded={setBdeOnboarded}
                        saveBmdeOnboarded={setBmdeOnboarded}
                        lastCompletedBdeStep={lastCompletedBdeStep}
                        lastCompletedBmdeStep={lastCompletedBmdeStep}
                        lastCompletedBmpOpStep={lastCompletedBmpopStep}
                        lastCompletedBarStep={lastCompletedBarStep}
                        saveLastCompletedBdeStep={setLastCompletedBdeStep}
                        saveLastCompletedBmdeStep={setLastCompletedBmdeStep}
                        saveLastCompletedBmpOpStep={setLastCompletedBmpopStep}
                        saveLastCompletedBarStep={setLastCompletedBarStep}
                        halfSizedCard={false}
                        fetchedOnboardingImages={fetchedOnboardingImages}
                        bmpOpOnboarded={bmpOpOnboarded}
                        saveBmpOpOnboarded={setBmpOpOnboarded}
                        barOnboarded={barOnboarded}
                        saveBarOnboarded={setBarOnboarded}
                        bmweOnboarded={bmweOnboarded}
                        saveBmweOnboarded={setBmweOnboarded}
                        lastCompletedBmweStep={lastCompletedBmweStep}
                        saveLastCompletedBmweStep={setLastCompletedBmweStep}
                    />
                </div>
            ))}
        </>
    );

    useEffect(() => {
        if (!initialLoadComplete.current && displayedProducts) {
            initialLoadComplete.current = true;
        }
    }, [displayedProducts]);

    const productCards = (
        <AnimatePresence>
            {displayedProducts &&
                Object.entries(displayedProducts).map(([key, value]) => (
                    <motion.div
                        key={key}
                        initial={initialLoadComplete.current ? { opacity: 0, y: 20 } : false}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            opacity: { duration: 0.3, ease: "easeIn", delay: 0.1 },
                            y: { duration: 0.3, ease: "easeIn" },
                        }}
                        className={style.tableCell}
                    >
                        <ProductCard
                            name={key}
                            softwarePackages={value}
                            fetchedSoftwarePackages={fetchedSoftwarePackages}
                            fetchedLicenses={licenseList}
                            bdeOnboarded={bdeOnboarded}
                            bmdeOnboarded={bmdeOnboarded}
                            saveBdeOnboarded={setBdeOnboarded}
                            saveBmdeOnboarded={setBmdeOnboarded}
                            lastCompletedBdeStep={lastCompletedBdeStep}
                            lastCompletedBmdeStep={lastCompletedBmdeStep}
                            lastCompletedBmpOpStep={lastCompletedBmpopStep}
                            lastCompletedBarStep={lastCompletedBarStep}
                            saveLastCompletedBdeStep={setLastCompletedBdeStep}
                            saveLastCompletedBmdeStep={setLastCompletedBmdeStep}
                            saveLastCompletedBmpOpStep={setLastCompletedBmpopStep}
                            saveLastCompletedBarStep={setLastCompletedBarStep}
                            halfSizedCard={false}
                            fetchedOnboardingImages={fetchedOnboardingImages}
                            bmpOpOnboarded={bmpOpOnboarded}
                            saveBmpOpOnboarded={setBmpOpOnboarded}
                            barOnboarded={barOnboarded}
                            saveBarOnboarded={setBarOnboarded}
                            bmweOnboarded={bmweOnboarded}
                            saveBmweOnboarded={setBmweOnboarded}
                            lastCompletedBmweStep={lastCompletedBmweStep}
                            saveLastCompletedBmweStep={setLastCompletedBmweStep}
                        />
                    </motion.div>
                ))}
        </AnimatePresence>
    );
    return loading ? (
        <LoadingIndicator isView />
    ) : (
        <>
            <div className={style.planAndSupport}>
                {isLicensingModelBundleWithToken() ? <CurrentPlan tenantData={currentTenant} /> : null}
                <Support tenantTier={currentTenant.tenantTier} trialTenant={currentTenant.contractType === "TRIAL"} />
                {!isInTenantAccess() && currentTenant.contractType === "TRIAL" ? (
                    <ProductGuideHelp setShowTrialVideo={setShowTrialVideo} />
                ) : null}
            </div>
            <HomeFilters mainProducts={mainProductsState} onFilterChange={setDisplayedProducts} />
            <div className={style.products}>
                {productCards}
                <hr className={style.hr} />
                {additionsProductCards}
            </div>
            {modal}
        </>
    );
};

export default connector(Products);
